import { isObjectLike } from '@crown/framework/ir';
import type { OutletContent, RouteConfig } from '../types';

export interface PreloadContext {
  routes: RouteConfig[];
  currentRoute: string;
  prefetchCache: Map<string, OutletContent | null>;
  isIdle: boolean;
  isNetworkIdle: boolean;
  callStackDepth: number;
  networkType: string;
}
export interface PreloadStrategy {
  name: string;
  selectRoutes: (ctx: PreloadContext) => RouteConfig[];
  shouldPreload: (ctx: PreloadContext) => boolean;
  prioritize?: (routes: RouteConfig[], ctx: PreloadContext) => RouteConfig[];
  maxConcurrent?: number;
  delay?: number;
}
export const NoPreloading: PreloadStrategy = {
  name: 'NoPreloading',
  selectRoutes: () => [],
  shouldPreload: () => false,
};
/**
 * What a router is told to preload with: a strategy, or a strategy's name.
 * No strategy is registered by name, so a name selects no preloading.
 */
export type PreloadStrategySetting = PreloadStrategy | string;

export function getPreloadStrategy(
  strategy: PreloadStrategySetting | undefined,
): PreloadStrategy {
  // A configuration written in JavaScript can hand over anything; only an
  // object carrying a name is taken for a strategy.
  if (isObjectLike(strategy) && 'name' in strategy) return strategy;
  return NoPreloading;
}
export function createStrategyExecutor({
  prefetchRoute,
  getPreloadContext,
}: {
  prefetchRoute: (path: string, route: RouteConfig) => Promise<void>;
  getPreloadContext: (routes: RouteConfig[], current: string) => PreloadContext;
}) {
  let currentStrategy: PreloadStrategy = NoPreloading;
  const executionTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  async function executeStrategy(
    allRoutes: RouteConfig[],
    currentRoute: string,
  ) {
    for (const t of executionTimeouts.values()) clearTimeout(t);
    executionTimeouts.clear();
    const context = getPreloadContext(allRoutes, currentRoute);
    if (
      !currentStrategy.shouldPreload ||
      !currentStrategy.shouldPreload(context)
    )
      return;
    const routesToPreload: RouteConfig[] =
      currentStrategy.selectRoutes(context) || [];
    const prioritized = currentStrategy.prioritize
      ? currentStrategy.prioritize(routesToPreload, context)
      : routesToPreload;
    const maxConcurrent = currentStrategy.maxConcurrent || 2;
    const delay = currentStrategy.delay || 0;
    let active = 0;
    let idx = 0;
    async function next() {
      if (idx >= prioritized.length) return;
      const route = prioritized[idx++];
      if (!route) return;
      active++;
      try {
        await prefetchRoute(route.path, route);
      } finally {
        active--;
        if (idx < prioritized.length && active < maxConcurrent) {
          if (delay > 0)
            executionTimeouts.set(route.path, setTimeout(next, delay));
          else next();
        }
      }
    }
    const initial = Math.min(maxConcurrent, prioritized.length);
    for (let i = 0; i < initial; i++) next();
  }
  function setStrategy(strategy: PreloadStrategySetting) {
    currentStrategy = getPreloadStrategy(strategy);
  }
  function getStrategy(): PreloadStrategy {
    return currentStrategy;
  }
  return { executeStrategy, setStrategy, getStrategy };
}
